import useWebSocket, { ReadyState } from "react-use-websocket";
import { useCallback, useEffect, useState } from "react";
import { Guideline } from "../graphql/types/graphql";

const yes = () => true;

export function useStreamingAnswer(
  endpoint: string,
  wsSessionId: string,
  questionType: string,
) {
  const [connectionId, setConnectionId] = useState<string>();
  const [answer, setAnswer] = useState<string[]>([]);
  const [metadata, setMetadata] = useState<Record<string, any>>({});
  const [isStreaming, setStreaming] = useState<boolean>(false);

  const consumeConnectionMsg = useCallback(
    (event: MessageEvent) => {
      // grab connectionId passed from server
      try {
        const obj = JSON.parse(event.data);
        if (obj?.connectionId) {
          console.log("WS Received ConnectionID", obj);
          setConnectionId(obj.connectionId);
        }
      } catch (e) {}
    },
    [setConnectionId],
  );

  const filterMsgs = useCallback(
    (event: MessageEvent) => {
      try {
        const obj = JSON.parse(event.data);
        // filter events that are for a different session or different question
        if (
          obj?.questionId !== questionType ||
          (obj?.sessionid && wsSessionId && obj?.sessionid !== wsSessionId)
        )
          return false;
        return obj?.action === "answer";
      } catch (e) {
        return false;
      }
    },
    [questionType, wsSessionId],
  );

  const { lastJsonMessage, sendMessage, readyState } = useWebSocket(
    endpoint || null,
    {
      share: true,
      onMessage: consumeConnectionMsg,
      filter: filterMsgs,
      shouldReconnect: yes,
      heartbeat: true,
    },
  );

  // on first connection, get connection id
  useEffect(() => {
    if (readyState === ReadyState.OPEN && !connectionId) {
      // ask for connection id (actual msg doesnt matter)
      // the server will send back the connectionid, which will be handled in the onMessage callback
      console.log("WS Asked for ConnectionID");
      sendMessage("get connectionid");
    }
  }, [connectionId, readyState, sendMessage]);

  // on new chunk update answer
  useEffect(() => {
    if (lastJsonMessage) {
      const { start, end, chunk, index, metadata } = lastJsonMessage as any;
      if (start) {
        setStreaming(true);
        setAnswer([]);
        setMetadata({});
      } else if (chunk !== null && chunk !== undefined) {
        setStreaming(true);
        setAnswer((existing) => {
          const updated = [...(existing || [])];
          updated[index] = chunk;
          return updated;
        });
      } else if (metadata) {
        setMetadata((existing = {}) => ({ ...existing, ...metadata }));
      } else if (end) {
        setStreaming(false);
        setAnswer([]); //reset answer after end
        setMetadata({});
      }
    }
  }, [lastJsonMessage]);
  // return the answer for which we have all parts in order
  const missingChunk = answer.findIndex((e) => e === undefined);
  const combinedAnswer = answer
    .slice(0, missingChunk > -1 ? missingChunk : undefined)
    .join("");
  return {
    answer: combinedAnswer,
    guidelines: (metadata?.guidelines || []) as Guideline[],
    altGuidelines: (metadata?.altGuidelines || []) as Guideline[],
    lastChunk: (lastJsonMessage as any)?.chunk,
    streamId: connectionId,
    isStreaming,
  };
}
